/**
 * Module und BETA — nur für SYSTEM.
 *
 * Bedienung unverändert (PR-5X-E): Klick auf eine Zeile öffnet das
 * Bearbeiten-Fenster, kein Inline-Bearbeiten, kein Zahnrad. Umgebaut wurde
 * allein die Darstellung — Rahmen, Karte und Tabelle des Leitstands, Farben
 * ausschließlich aus der Token-Schicht (styles/index.css).
 *
 * Zur Farbe im Reifegrad: Sie sagt hier Zustand, nicht Rang. „Aus" ist
 * deshalb leise gesetzt und nicht rot — ein abgeschaltetes Modul ist eine
 * Entscheidung, keine Störung.
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import {
  Karte,
  Kennung,
  Kopfzelle,
  Notiz,
  Tabelle,
  Zelle,
  Zustand,
} from '../../components/leitstand';
import { useModules } from '../../hooks/useModules';
import type { ModuleMaturity } from '../../hooks/useModules';

const EINGABE =
  'w-full rounded-[4px] border border-linie-2 bg-flaeche-2 px-3 py-2 text-sm text-tinte focus:border-akzent focus:outline-none';
const KNOPF = 'rounded-[4px] px-3 py-1.5 font-zahl text-xs disabled:opacity-50';
const KNOPF_PRIMAER = `${KNOPF} border border-akzent bg-akzent text-akzent-satz`;
const KNOPF_LEISE = `${KNOPF} border border-linie-2 bg-flaeche text-tinte-2`;
const FELD_MARKE = 'font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3';

/** Reifegrad als Zustand: je unfertiger, desto lauter. Standard bleibt still. */
const REIFE_KLASSE: Record<ModuleMaturity, string> = {
  STABLE: 'border-linie-2 text-tinte-2',
  BETA: 'border-stand/50 bg-stand/10 text-stand',
  EXPERIMENTAL: 'border-stoer/50 bg-stoer/10 text-stoer',
  DEPRECATED: 'border-linie-2 bg-flaeche-2 text-tinte-3',
};

interface ModuleData {
  key: string;
  name: string;
  description: string;
  tooltip: string;
  enabled: boolean;
  maturity: ModuleMaturity;
  hasOverride: boolean;
  overrideEnabled: boolean | null;
  overrideMaturity: ModuleMaturity | null;
  overrideTooltip: string | null;
  defaultEnabled: boolean;
  defaultMaturity: ModuleMaturity;
}

export default function ModuleFlagsPage() {
  const { user } = useAuth();
  const { modules, loading, error, refresh } = useModules();

  // Zustand des Bearbeiten-Fensters
  const [editingModule, setEditingModule] = useState<ModuleData | null>(null);
  const [formEnabled, setFormEnabled] = useState<boolean | null>(null);
  const [formMaturity, setFormMaturity] = useState<ModuleMaturity | null>(null);
  const [formTooltip, setFormTooltip] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Zugriff: nur SYSTEM
  if (!user || user.role !== 'SYSTEM') {
    return (
      <LeitstandRahmen titel="Module und BETA" benutzer={user?.displayName}>
        <Notiz titel="Kein Zugriff">
          Diese Seite ist dem Systemzugang vorbehalten. Wenden Sie sich an Ihre Betreuung, wenn ein
          Modul ein- oder ausgeschaltet werden soll.
        </Notiz>
      </LeitstandRahmen>
    );
  }

  const openEditDialog = (module: ModuleData) => {
    setEditingModule(module);
    setFormEnabled(module.overrideEnabled);
    setFormMaturity(module.overrideMaturity);
    setFormTooltip(module.overrideTooltip ?? '');
    setSaveError(null);
  };

  const closeDialog = () => {
    setEditingModule(null);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!editingModule) return;

    setSaving(true);
    setSaveError(null);

    try {
      const response = await fetch(`/api/system/modules/${editingModule.key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          enabled: formEnabled,
          maturity: formMaturity,
          tooltip: formTooltip || null,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Speichern fehlgeschlagen');
      }

      await refresh();
      closeDialog();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Unbekannter Fehler');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!editingModule) return;

    setSaving(true);
    setSaveError(null);

    try {
      const response = await fetch(`/api/system/modules/${editingModule.key}/reset`, {
        method: 'PUT',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Zuruecksetzen fehlgeschlagen');
      }

      await refresh();
      closeDialog();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Unbekannter Fehler');
    } finally {
      setSaving(false);
    }
  };

  return (
    <LeitstandRahmen titel="Module und BETA" benutzer={user.displayName}>
      <div className="flex max-w-[1000px] flex-col gap-5">
        <p className="text-sm text-tinte-2">
          Module im laufenden Betrieb ein- und ausschalten und ihren Reifegrad setzen.
        </p>

        <Notiz titel="Hinweis">
          Ausgeschaltete Module sind für alle außer dem Systemzugang vollständig unsichtbar. Module
          mit dem Reifegrad BETA oder EXPERIMENTAL tragen ein Kennzeichen in der Navigation.
        </Notiz>

        <Karte titel="Module" marke="Zeile anklicken zum Bearbeiten" tabelle>
          {loading ? (
            <p className="py-8 text-center text-sm text-tinte-3">Lade Module …</p>
          ) : error ? (
            <p className="py-8 text-center text-sm text-stoer">Konnte nicht laden: {error}</p>
          ) : modules.length === 0 ? (
            <p className="py-8 text-center text-sm text-tinte-3">Keine Module vorhanden.</p>
          ) : (
            <Tabelle>
              <thead>
                <tr>
                  <Kopfzelle>Modul</Kopfzelle>
                  <Kopfzelle>Status</Kopfzelle>
                  <Kopfzelle>Reifegrad</Kopfzelle>
                  <Kopfzelle>Abweichung</Kopfzelle>
                </tr>
              </thead>
              <tbody>
                {modules.map((module, i) => {
                  const letzte = i === modules.length - 1;
                  return (
                    <tr
                      key={module.key}
                      className="cursor-pointer hover:bg-flaeche-2"
                      onClick={() => openEditDialog(module as ModuleData)}
                    >
                      <Zelle letzte={letzte}>
                        <span className="block font-medium">{module.name}</span>
                        <span className="block font-zahl text-[11px] text-tinte-3">
                          {module.key}
                        </span>
                      </Zelle>
                      <Zelle letzte={letzte}>
                        {module.enabled ? (
                          <Zustand art="gut">eingeschaltet</Zustand>
                        ) : (
                          <span className="font-zahl text-[11px] text-tinte-3">ausgeschaltet</span>
                        )}
                      </Zelle>
                      <Zelle letzte={letzte}>
                        <span
                          className={`rounded-[4px] border px-[7px] py-[3px] font-zahl text-[10px] tracking-[0.08em] ${
                            REIFE_KLASSE[module.maturity] ?? REIFE_KLASSE.STABLE
                          }`}
                        >
                          {module.maturity}
                        </span>
                      </Zelle>
                      <Zelle letzte={letzte}>
                        {module.hasOverride ? (
                          <Kennung>Ja</Kennung>
                        ) : (
                          <span className="font-zahl text-[11px] text-tinte-3">—</span>
                        )}
                      </Zelle>
                    </tr>
                  );
                })}
              </tbody>
            </Tabelle>
          )}
        </Karte>
      </div>

      {/* Bearbeiten-Fenster — wie bisher: schließt nur über Abbrechen oder ✕ */}
      {editingModule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-grund/85 p-4 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-leitstand border border-linie-2 bg-flaeche">
            <div className="flex items-center justify-between gap-3 border-b border-linie px-[18px] py-[14px]">
              <h2 className="text-sm font-semibold">Modul bearbeiten: {editingModule.name}</h2>
              <button
                onClick={closeDialog}
                className="rounded-[4px] p-1 text-tinte-3 hover:text-tinte"
                aria-label="Schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-col gap-4 p-[18px]">
              {saveError && <p className="text-[13px] text-stoer">{saveError}</p>}

              <div className="flex flex-col gap-1 rounded-[4px] border border-linie bg-flaeche-2 px-3 py-2.5 text-[13px] text-tinte-2">
                <span>
                  <span className={FELD_MARKE}>Schlüssel</span>{' '}
                  <span className="font-zahl text-tinte">{editingModule.key}</span>
                </span>
                <span>
                  <span className={FELD_MARKE}>Standard</span>{' '}
                  <span className="text-tinte">
                    {editingModule.defaultEnabled ? 'eingeschaltet' : 'ausgeschaltet'},{' '}
                    {editingModule.defaultMaturity}
                  </span>
                </span>
              </div>

              <label className="flex flex-col gap-1.5">
                <span className={FELD_MARKE}>Status</span>
                <select
                  value={formEnabled === null ? 'null' : formEnabled ? 'true' : 'false'}
                  onChange={(e) =>
                    setFormEnabled(e.target.value === 'null' ? null : e.target.value === 'true')
                  }
                  className={EINGABE}
                >
                  <option value="null">
                    Standard ({editingModule.defaultEnabled ? 'eingeschaltet' : 'ausgeschaltet'})
                  </option>
                  <option value="true">Eingeschaltet</option>
                  <option value="false">Ausgeschaltet</option>
                </select>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className={FELD_MARKE}>Reifegrad</span>
                <select
                  value={formMaturity ?? 'null'}
                  onChange={(e) =>
                    setFormMaturity(
                      e.target.value === 'null' ? null : (e.target.value as ModuleMaturity),
                    )
                  }
                  className={EINGABE}
                >
                  <option value="null">Standard ({editingModule.defaultMaturity})</option>
                  <option value="STABLE">STABLE</option>
                  <option value="BETA">BETA</option>
                  <option value="EXPERIMENTAL">EXPERIMENTAL</option>
                  <option value="DEPRECATED">DEPRECATED</option>
                </select>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className={FELD_MARKE}>Kurzerklärung (freiwillig)</span>
                <input
                  type="text"
                  value={formTooltip}
                  onChange={(e) => setFormTooltip(e.target.value)}
                  placeholder="z. B. BETA: Funktion im Aufbau …"
                  className={EINGABE}
                />
                <span className="text-xs text-tinte-3">
                  Erscheint am Kennzeichen in der Navigation. Leer lassen für den Standardtext.
                </span>
              </label>
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-linie px-[18px] py-[14px]">
              <div>
                {editingModule.hasOverride && (
                  <button onClick={handleReset} disabled={saving} className={KNOPF_LEISE}>
                    Auf Standard zurücksetzen
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={closeDialog} className={KNOPF_LEISE}>
                  Abbrechen
                </button>
                <button onClick={handleSave} disabled={saving} className={KNOPF_PRIMAER}>
                  {saving ? 'Speichert …' : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </LeitstandRahmen>
  );
}
